import request from './request'
import { webSignHeaders, apiBase } from '@/utils/sign'
import { stripHtml, formatExcerpt } from './mock/data'

let conversationId = null
let conversationPromise = null

function ok(data) {
  return Promise.resolve({ code: 0, data })
}

async function signedFetch(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...webSignHeaders(),
    ...(options.headers || {})
  }
  return fetch(apiBase() + path, { ...options, headers })
}

async function signedJson(path, body) {
  const res = await signedFetch(path, {
    method: 'POST',
    body: JSON.stringify(body)
  })
  const data = await res.json()
  if (data.code !== 200) throw new Error(data.msg || '请求失败')
  return { code: 0, data: data.data, msg: data.msg }
}

/** 19. POST /Ask/Conversation */
export async function createConversation() {
  if (conversationId) return conversationId
  if (conversationPromise) return conversationPromise
  conversationPromise = signedFetch('/Ask/Conversation', { method: 'POST' })
    .then(res => res.json())
    .then(body => {
      if (body.code !== 200) throw new Error(body.msg || '创建会话失败')
      conversationId = body.data.conversation_id
      return conversationId
    })
    .finally(() => { conversationPromise = null })
  return conversationPromise
}

export function resetConversation() {
  conversationId = null
}

export function mapSources(sources = []) {
  const map = new Map()
  sources.forEach(s => {
    const docId = s.document_id
    if (!docId) return
    const score = s.score || 0
    const prev = map.get(docId)
    if (!prev || score > (prev._score || 0)) {
      map.set(docId, {
        documentId: docId,
        title: s.filename || docId,
        content: s.snippet || '',
        _matchType: 'semantic',
        _score: score
      })
    }
  })
  return Array.from(map.values())
}

function mapAskData(data) {
  return {
    type: data.knowledge_hit ? 'hit' : 'miss',
    text: data.answer || '',
    hitId: (data.sources && data.sources[0] && data.sources[0].document_id) || null,
    askRecordId: data.ask_record_id,
    conversationId: data.conversation_id,
    sources: data.sources || [],
    knowledgeHit: data.knowledge_hit
  }
}

/** 20. POST /Ask */
export async function askPlain(question, convId) {
  const conversation_id = convId || await createConversation()
  const res = await signedJson('/Ask', { conversation_id, question })
  return mapAskData(res.data)
}

function parseSseEvents(buffer) {
  const events = []
  const chunks = buffer.split('\n\n')
  const rest = chunks.pop() || ''
  chunks.forEach(chunk => {
    let event = 'message'
    let data = ''
    chunk.split('\n').forEach(line => {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) data += line.slice(5).trim()
    })
    if (data) {
      try { events.push({ event, data: JSON.parse(data) }) }
      catch (e) { events.push({ event, data }) }
    }
  })
  return { events, rest }
}

/** 21. POST /Ask/Stream */
async function askStream(question, convId, onDelta) {
  const res = await signedFetch('/Ask/Stream', {
    method: 'POST',
    headers: { Accept: 'text/event-stream' },
    body: JSON.stringify({ conversation_id: convId, question })
  })
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}))
    throw new Error(errBody.msg || '流式问答失败')
  }
  const contentType = res.headers.get('content-type') || ''
  if (contentType.indexOf('text/event-stream') < 0) {
    const body = await res.json()
    if (body.code !== 200) throw new Error(body.msg || '问答失败')
    return mapAskData(body.data)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result = {
    type: 'hit', text: '', hitId: null, askRecordId: null,
    conversationId: convId, sources: [], knowledgeHit: false
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const parsed = parseSseEvents(buffer)
    buffer = parsed.rest
    parsed.events.forEach(({ event, data }) => {
      if (event === 'start' || event === 'meta') {
        result.askRecordId = data.ask_record_id
        result.conversationId = data.conversation_id || convId
      }
      if (event === 'sources') {
        result.sources = data.sources || []
        result.knowledgeHit = data.knowledge_hit
        result.hitId = (data.sources && data.sources[0] && data.sources[0].document_id) || null
      }
      if (event === 'delta') {
        result.text += data.text || ''
        if (onDelta) onDelta(data.text || '')
      }
      if (event === 'done') {
        result.text = data.answer || result.text
        result.sources = data.sources || result.sources
        result.knowledgeHit = data.knowledge_hit
        result.hitId = (data.sources && data.sources[0] && data.sources[0].document_id) || null
        result.askRecordId = data.ask_record_id
        result.conversationId = data.conversation_id || convId
      }
      if (event === 'error') throw new Error((data && data.detail) || '问答出错')
    })
  }
  result.type = result.knowledgeHit ? 'hit' : 'miss'
  return result
}

/** 11. GET /Knowledge/Detail（C 端按 document_id 查详情，id 非必填） */
export const knowledgeApi = {
  getByDocumentId(documentId) {
    return request.get('/Knowledge/Detail', {
      params: { document_id: documentId }
    }).then(res => {
      const d = res.data || {}
      return {
        code: 0,
        data: {
          id: d.id,
          kbId: d.kb_id,
          title: d.title || '',
          purpose: d.purpose || '',
          content: d.content || '',
          tags: d.tags || '',
          status: d.status
        }
      }
    })
  }
}

/** 18. GET /PresetContent/PublicList */
export const presetApi = {
  list() {
    return request.get('/PresetContent/PublicList').then(res => {
      const list = (res.data && res.data.list) || []
      return ok(list.map(q => ({ q })))
    })
  }
}

export const chatApi = {
  askPlain(question) {
    const q = (question || '').trim()
    if (!q) return ok({ type: 'miss', text: '请输入问题', hitId: null, sources: [] })
    return askPlain(q).then(data => ok(data)).catch(e => ok({ type: 'miss', text: e.message, hitId: null, sources: [] }))
  },
  async ask(question, context = {}, onDelta) {
    const q = (question || '').trim()
    if (!q) return ok({ type: 'miss', text: '请输入问题', hitId: null })
    try {
      const convId = await createConversation()
      const data = await askStream(q, convId, onDelta)
      return ok(data)
    } catch (e) {
      return ok({ type: 'miss', text: e.message || '问答服务暂不可用', hitId: null })
    }
  }
}

/** 22. POST /Ask/Feedback */
export const feedbackApi = {
  async submit(payload) {
    const conversation_id = payload.conversationId || await createConversation()
    await request.post('/Ask/Feedback', {
      ask_record_id: payload.askRecordId,
      conversation_id,
      question: payload.question,
      feedback_type: payload.type === 'like' ? 1 : 2,
      handle_status: 1,
      reason: payload.reason || ''
    })
    return ok(null)
  }
}

export { stripHtml, formatExcerpt, createConversation as ensureConversation }
